/** Country / category overview. Real countries and other categories read together —
    one count, one list, one pair of charts — with `kind` on each row telling them
    apart, which is all the separation this page turned out to need.

    Both charts are also the way into the list: clicking a bar or a slice narrows the
    table below to that entry, and both charts show the same selection. See `selected`. */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '../App'
import { api, useApi } from '../lib/api'
import { BarsChart, DonutChart } from '../components/charts'
import { EnumSelect, CountrySelect } from '../components/Select'
import {
  Card, Tile, Badge, Field, Modal, Notice, Spinner, QueryState, Empty,
  useToast, fmtInt, relDays, downloadCsv,
} from '../components/ui'
import { useRelabel } from '../lib/terms'

/**
 * The source data files an organisational bucket in the same table as a real
 * country, so this page can create either. `kind` is what keeps them apart —
 * only 'country' rows count towards the country total and the geographic reports.
 */
const KINDS = [
  { value: 'country', label: 'Country', hint: 'A real geographic country. The only kind counted in the country total and the geographic reports.' },
  { value: 'office', label: 'Office', hint: 'An office rather than a place, e.g. the home office.' },
  { value: 'group', label: 'Grouping', hint: 'A set of partner units, e.g. teams or non-staff partner units.' },
  { value: 'region', label: 'Region', hint: 'A broader area, e.g. partner units outside Central Asia.' },
  { value: 'status', label: 'Status', hint: 'A lifecycle bucket, e.g. alumni or inactive.' },
]

const blank = { name: '', kind: 'country', iso2: '', flag_emoji: '' }

function AddCountryModal({ open, onClose, onSaved }) {
  const toast = useToast()
  const [form, setForm] = useState(blank)
  const [busy, setBusy] = useState(false)
  const set = (patch) => setForm((f) => ({ ...f, ...patch }))
  const isCountry = form.kind === 'country'
  const kind = KINDS.find((k) => k.value === form.kind)

  const submit = async () => {
    setBusy(true)
    try {
      await api('countries', {
        method: 'POST',
        body: {
          name: form.name,
          kind: form.kind,
          iso2: isCountry ? form.iso2 : null,
          flag_emoji: form.flag_emoji,
        },
      })
      toast.ok(`${form.name} added`)
      setForm(blank)
      onSaved()
      onClose()
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add a country or grouping"
      footer={
        <>
          <button className="btn btn-primary" onClick={submit} disabled={busy || !form.name.trim()}>
            {busy ? <Spinner /> : 'Add entry'}
          </button>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
        </>
      }
    >
      <div className="col">
        <Notice icon="info">
          The new entry becomes selectable on every partner unit and update form straight away.
          Nothing is moved into it — reassign partner units from their own pages.
        </Notice>
        <Field label="Name" required>
          <input
            className="input"
            value={form.name}
            onChange={(e) => set({ name: e.target.value })}
            placeholder={isCountry ? 'e.g. Mongolia' : 'e.g. X CA Interns'}
          />
        </Field>
        <Field label="Kind" required hint={kind?.hint}>
          <EnumSelect
            value={form.kind}
            onChange={(v) => set({ kind: v || 'country' })}
            options={KINDS.map((k) => ({ value: k.value, label: k.label }))}
            isClearable={false}
          />
        </Field>
        <div className="field-row">
          {isCountry && (
            <Field label="ISO code" hint="Two letters, e.g. MN. Optional.">
              <input
                className="input mono"
                value={form.iso2}
                onChange={(e) => set({ iso2: e.target.value.toUpperCase().slice(0, 2) })}
                placeholder="MN"
              />
            </Field>
          )}
          <Field label="Flag or icon" hint="One emoji, shown next to the name throughout the app.">
            <input
              className="input"
              value={form.flag_emoji}
              onChange={(e) => set({ flag_emoji: e.target.value })}
              placeholder={isCountry ? '🇲🇳' : '👥'}
            />
          </Field>
        </div>
      </div>
    </Modal>
  )
}

export default function CountryList({ user }) {
  const t = useRelabel()
  const query = useApi('countries', 'countries')
  const [addOpen, setAddOpen] = useState(false)
  /* The entry the charts and the list are showing, as a country_id. Held here rather
     than per chart so the bar, the slice and the picker are three ways at one selection
     instead of three selections that can disagree. */
  const [selected, setSelected] = useState(null)
  const qc = useQueryClient()

  return (
    <PageHeader
      title="Country / Category"
      sub="Countries, offices and groupings — where partner units are registered and where updates come from"
      actions={
        user?.caps.create ? (
          <button className="btn btn-sm btn-primary" onClick={() => setAddOpen(true)}>
            + New Country / Category
          </button>
        ) : null
      }
    >
      <QueryState query={query}>
        {(d) => {
          // The table is a directory — you come to it with a name in mind — so it reads
          // by name. The charts below rank by size, which is what a chart is for.
          const byName = (rows) =>
            rows.slice().sort((a, b) => a.country_name.localeCompare(b.country_name, undefined, { sensitivity: 'base' }))
          const real = byName(d.rows.filter((r) => r.kind === 'country'))
          const other = byName(d.rows.filter((r) => r.kind !== 'country'))
          // The two kinds still count separately in the tile's note; the list is one.
          const shown = byName(d.rows)
          const totalNewsletters = d.rows.reduce((a, r) => a + r.newsletter_count, 0)
          // Both charts read every category, not only the geographic ones: a partner
          // filed under the home office or a team is as much of this directory as one
          // filed under Kazakhstan, and leaving them out lost a slice of the archive.
          const byUpdates = d.rows.slice().sort((a, b) => b.newsletter_count - a.newsletter_count)

          /* The charts speak in names, since a name is what an axis and a legend can
             show; the selection is an id, because that is what identifies a row and what
             the picker reports. This is the one place the two meet. Nothing is picked if
             the name is not one of ours — a click on empty chart space. */
          const pickByName = (name) => {
            const row = d.rows.find((r) => r.country_name === name)
            if (row) setSelected((cur) => (cur === row.country_id ? null : row.country_id))
          }
          const active = selected == null ? null : d.rows.find((r) => r.country_id === selected)
          // A selection that is no longer in the data — the entry was renamed or removed
          // under us — highlights nothing and filters nothing, rather than emptying the
          // list with no way to see why.
          const listed = active ? shown.filter((r) => r.country_id === active.country_id) : shown

          return (
            <div className="col">
              {/* One count for the directory rather than a geographic tile beside a
                  non-geographic one: the two were only ever read as a total, and the
                  kinds are on the rows below. */}
              <div className="grid grid-4">
                <Tile
                  label="Country / categories"
                  value={fmtInt(d.rows.length)}
                  note={`${fmtInt(real.length)} countries, ${fmtInt(other.length)} other`}
                />
                <Tile label="Updates placed" value={fmtInt(totalNewsletters)} />
              </div>

              <div className="grid grid-2">
                <Card
                  title="Updates by Country / Category"
                  sub="Every entry, geographic or not — click one to narrow the list below"
                >
                  <BarsChart
                    data={byUpdates.filter((r) => r.newsletter_count > 0)}
                    x="country_name"
                    series={[{ key: 'newsletter_count', label: 'Updates' }]}
                    horizontal
                    height={320}
                    onPick={pickByName}
                    activeX={active?.country_name}
                  />
                </Card>
                <Card
                  title="Partner Units by Country / Category"
                  sub="Every entry, geographic or not — click a slice to narrow the list below"
                >
                  <DonutChart
                    data={d.rows
                      .filter((r) => r.partner_count > 0)
                      .map((r) => ({ name: r.country_name, value: r.partner_count }))}
                    height={320}
                    onPick={pickByName}
                    activeName={active?.country_name}
                  />
                </Card>
              </div>

              <Card
                title={active ? active.country_name : 'All Entries'}
                actions={
                  <>
                    {/* The same selection the charts set, as a control that can be
                        reached and cleared from the keyboard — a click on an SVG cannot
                        be. It lists every entry, grouped into countries and the rest. */}
                    <div style={{ width: 220 }}>
                      <CountrySelect
                        inputId="cc-selected"
                        value={selected}
                        onChange={setSelected}
                        placeholder="Every entry"
                      />
                    </div>
                    <button
                      className="btn btn-sm"
                      onClick={() =>
                        downloadCsv('countries.csv', byName(listed), [
                          { key: 'country_name', label: 'Name' },
                          { key: 'kind', label: 'Kind' },
                          { key: 'partner_count', label: 'Partner Units' },
                          { key: 'newsletter_count', label: 'Updates' },
                          { key: 'highlighted_count', label: 'Highlighted' },
                          { key: 'last_newsletter', label: 'Latest' },
                        ])
                      }
                    >
                      ↓ CSV
                    </button>
                  </>
                }
                sub={
                  active
                    ? `One of ${fmtInt(shown.length)} entries — clear the picker, or click the bar again, for all of them`
                    : 'Countries and other categories together, by name'
                }
                bodyClass="card-body"
              >
                {/* One list, not two behind tabs. You come here with a name in mind and
                    not always the knowledge of which kind holds it, so a tab was a
                    guess you had to make before you could look; the Kind column says
                    which it is once you have found it. */}
                {!listed.length ? (
                  <Empty title={shown.length ? 'That entry is no longer here' : 'Nothing here'} />
                ) : (
                  <div className="table-wrap">
                    <table className="data">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Kind</th>
                          <th className="right">{t('Partner Units')}</th>
                          <th className="right">Updates</th>
                          <th className="right">Highlighted</th>
                          <th>Latest</th>
                        </tr>
                      </thead>
                      <tbody>
                        {listed.map((r) => (
                          <tr key={r.country_id}>
                            <td>
                              <Link to={`/countries/${r.country_id}`}>
                                {r.flag_emoji ? <span aria-hidden="true">{r.flag_emoji} </span> : null}
                                {r.country_name}
                              </Link>
                            </td>
                            <td>
                              <Badge>{r.kind}</Badge>
                            </td>
                            <td className="num">{fmtInt(r.partner_count)}</td>
                            <td className="num">{fmtInt(r.newsletter_count)}</td>
                            <td className="num">{fmtInt(r.highlighted_count)}</td>
                            <td className="nowrap small">
                              {r.last_newsletter ? (
                                <>
                                  {r.last_newsletter}
                                  <div className="tiny subtle">{relDays(r.last_newsletter)}</div>
                                </>
                              ) : (
                                <span className="subtle">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>

              <AddCountryModal
                open={addOpen}
                onClose={() => setAddOpen(false)}
                onSaved={() => {
                  query.refetch()
                  // Every country dropdown reads the shared lookups payload.
                  qc.invalidateQueries({ queryKey: ['lookups'] })
                }}
              />
            </div>
          )
        }}
      </QueryState>
    </PageHeader>
  )
}
